import React from 'react';
import { formatRoleName } from '../../../utils/roleFormatter';
import '../../../shared/components/common/ActorDisplay.css';
import './ComplaintTimeline.css';

function ComplaintTimeline({ timeline, loading, error }) {
  const [expandedEventIds, setExpandedEventIds] = React.useState(new Set());

  // Toggle single event expansion
  const toggleEventExpansion = (eventId) => {
    const newSet = new Set(expandedEventIds);
    if (newSet.has(eventId)) {
      newSet.delete(eventId);
    } else {
      newSet.add(eventId);
    }
    setExpandedEventIds(newSet);
  };

  // Toggle expand all / collapse all
  const toggleExpandAll = () => {
    if (expandedEventIds.size === timeline.length) {
      // All expanded, so collapse all
      setExpandedEventIds(new Set());
    } else {
      // Not all expanded, so expand all
      const allIds = new Set(timeline.map(event => event.id));
      setExpandedEventIds(allIds);
    }
  };

  // Map event types to icons and titles
  const getEventDisplay = (eventType) => {
    const displays = {
      'creation': { icon: '✨', title: 'Ticket Created', color: '#27ae60' },
      'status_change': { icon: '⚡', title: 'Status Changed', color: '#1565c0' },
      'assignment': { icon: '👤', title: 'Assigned', color: '#6a1b9a' },
      'reassignment': { icon: '🔄', title: 'Reassigned', color: '#e65100' }
    };
    return displays[eventType] || { icon: '📝', title: 'Updated', color: 'var(--dp-accent-primary)' };
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Format status value for display
  const formatStatusValue = (value) => {
    if (!value) return 'None';
    return value.replace(/_/g, ' ').toUpperCase();
  };

  // Auto-assignment records NULL changed_by; manual assignment/reassignment records the acting user.
  const isSystemActorEvent = (event) => {
    return event.changed_by_employee_id == null &&
      (event.event_type === 'assignment' || event.event_type === 'reassignment');
  };

  // Get display name for changed_by user, handling NULL for guest vs system auto-actions
  const getChangedByDisplayName = (event) => {
    if (event.changed_by_employee_id == null) {
      if (isSystemActorEvent(event)) {
        return 'System';
      }
      // Creation (and similar) with no user row: manual reporter context
      return event.reporter_full_name || 'Manual Reporter';
    }
    return event.changed_by_name || event.changed_by_employee_id;
  };

  // Get the guest user's employee ID (reporter_employee_id for creation events)
  const getGuestUserEmployeeId = (event) => {
    // For creation events of guest complaints, the reporter_employee_id is available
    return event.reporter_employee_id || null;
  };

  // Get the CSS class for the "changed by" badge (guest vs system auto-action vs authenticated user)
  const getChangedByBadgeClass = (event) => {
    if (event.changed_by_employee_id == null) {
      return isSystemActorEvent(event) ? 'changed-by-badge-system' : 'changed-by-badge-guest';
    }
    return 'changed-by-badge-user';
  };

  // Calculate elapsed time between two timestamps
  const getElapsedTime = (previousTimestamp, currentTimestamp) => {
    if (!previousTimestamp || !currentTimestamp) return null;

    try {
      const prev = new Date(previousTimestamp);
      const current = new Date(currentTimestamp);
      const diffMs = current - prev;

      // In reversed timeline, current event is newer than previous event (next in original)
      // So diffMs should be positive (current is after previous)
      // If negative, swap the calculation to get absolute difference
      const absDiffMs = Math.abs(diffMs);

      const minutes = Math.floor(absDiffMs / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) {
        return `${days}d ${hours % 24}h`;
      } else if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      } else {
        return 'just now';
      }
    } catch (e) {
      console.error('Error calculating elapsed time:', e);
      return null;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="complaint-timeline-wrapper">
        <h4 className="timeline-title">
          <span className="timeline-title-icon">📋</span>
          Status Timeline
        </h4>
        <div className="timeline-loading">
          <div className="timeline-loading-spinner"></div>
          <p>Loading timeline...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="complaint-timeline-wrapper">
        <h4 className="timeline-title">
          <span className="timeline-title-icon">📋</span>
          Status Timeline
        </h4>
        <div style={{
          padding: '1rem',
          background: '#fee2e2',
          borderRadius: '6px',
          border: '1px solid #fca5a5',
          color: '#991b1b',
          fontSize: '0.85rem'
        }}>
          ⚠️ {error}
        </div>
      </div>
    );
  }

  // Render empty state
  if (!timeline || timeline.length === 0) {
    return (
      <div className="complaint-timeline-wrapper">
        <h4 className="timeline-title">
          <span className="timeline-title-icon">📋</span>
          Status Timeline
        </h4>
        <div className="timeline-empty">
          <span className="timeline-empty-icon">📭</span>
          <p>No timeline events recorded yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="complaint-timeline-wrapper">
      <div className="timeline-title-row">
        <h4 className="timeline-title">
          <span className="timeline-title-icon">📋</span>
          Activity ({timeline.length})
        </h4>
        <button
          className="timeline-toggle-all-btn"
          onClick={toggleExpandAll}
          title={expandedEventIds.size === timeline.length ? "Collapse all events" : "Expand all events"}
        >
          {expandedEventIds.size === timeline.length ? '▼' : '▶'}
        </button>
      </div>

      <div className="timeline-container">
        {/* Reverse timeline to show newest events first */}
        {[...timeline].reverse().map((event, index, reversedArray) => {
          const display = getEventDisplay(event.event_type);
          // In reversed array, the next event (index + 1) is the chronologically previous event
          const nextEvent = index < reversedArray.length - 1 ? reversedArray[index + 1] : null;
          // Calculate elapsed time between current event and next event (for rendering below current)
          const elapsedTimeToNext = nextEvent ? getElapsedTime(event.changed_at, nextEvent.changed_at) : null;
          const isExpanded = expandedEventIds.has(event.id);

          // Determine if this is a terminal status event (closed or cancelled)
          const isTerminalStatus = event.event_type === 'status_change' &&
            (event.new_value === 'closed' || event.new_value === 'cancelled');
          const terminalStatusType = event.new_value; // 'closed' or 'cancelled'

          return (
            <div key={event.id || index}>

              <div className={`timeline-event ${isExpanded ? 'expanded' : 'collapsed'} ${isTerminalStatus ? 'terminal-status ' + terminalStatusType : ''}`}>
                {/* Event Header - Clickable to Expand/Collapse */}
                <div className="timeline-event-header-compact" onClick={() => toggleEventExpansion(event.id)}>
                  <span className="event-expand-toggle">{isExpanded ? '▼' : '▶'}</span>
                  <span className="event-icon">{display.icon}</span>
                  <span className="event-title">{display.title}</span>
                  {isTerminalStatus && (
                    <span className={`terminal-status-indicator ${terminalStatusType}`}>
                      {terminalStatusType === 'closed' ? '✓ Closed' : '✗ Cancelled'}
                    </span>
                  )}
                  <span className="event-timestamp">{formatDate(event.changed_at)}</span>
                </div>

              {/* Expandable Event Details */}
              {isExpanded && (
                <div className="event-details-expanded">
                  {/* Event Type Badge */}
                  <div className={`event-type-badge ${event.event_type}`}>
                    {event.event_type.replace('_', ' ')}
                  </div>

                  {/* Status Change Details */}
                  {event.event_type === 'status_change' && (
                    <div className="assignment-transition">
                      <span className={`status-badge old`}>
                        {formatStatusValue(event.old_value)}
                      </span>
                      <span className="assignment-transition-arrow">→</span>
                      <span className={`status-badge new`}>
                        {formatStatusValue(event.new_value)}
                      </span>
                    </div>
                  )}

                  {/* Assignment Details */}
                  {event.event_type === 'assignment' && (
                    <div className="assignment-transition">
                      <span className="assignment-from-text">Unassigned</span>
                      <span className="assignment-transition-arrow">→</span>
                      <span className="actor-display-inline actor-display-inline--compact">
                        <span className="actor-display-name">
                          {event.assigned_employee_name || event.new_value}
                        </span>
                        {event.assigned_employee_role && (
                          <span className="actor-role-badge">{formatRoleName(event.assigned_employee_role)}</span>
                        )}
                        <span className="employee-id-inline">({event.new_value})</span>
                      </span>
                      {event.reason && event.reason.toLowerCase().includes('system') && (
                        <span className="assignment-type-indicator">⚙️ System</span>
                      )}
                    </div>
                  )}

                  {/* Reassignment Details */}
                  {event.event_type === 'reassignment' && (
                    <div className="assignment-transition">
                      <span className="actor-display-inline actor-display-inline--compact">
                        <span className="actor-display-name actor-display-name--muted">
                          {event.old_assigned_employee_name || event.old_value}
                        </span>
                        {event.old_assigned_employee_role && (
                          <span className="actor-role-badge">{formatRoleName(event.old_assigned_employee_role)}</span>
                        )}
                        <span className="employee-id-inline">({event.old_value})</span>
                      </span>
                      <span className="assignment-transition-arrow">→</span>
                      {event.new_value ? (
                        <span className="actor-display-inline actor-display-inline--compact">
                          <span className="actor-display-name">
                            {event.assigned_employee_name || event.new_value}
                          </span>
                          {event.assigned_employee_role && (
                            <span className="actor-role-badge">{formatRoleName(event.assigned_employee_role)}</span>
                          )}
                          <span className="employee-id-inline">({event.new_value})</span>
                        </span>
                      ) : (
                        <span className="assignment-from-text">Unassigned</span>
                      )}
                      {event.reason && event.reason.toLowerCase().includes('system') && (
                        <span className="assignment-type-indicator">⚙️ System</span>
                      )}
                    </div>
                  )}

                  {/* Changed By Information */}
                  <div className="changed-by-info">
                    <span className="changed-by-icon">
                      {event.changed_by_employee_id ? '👤' : (isSystemActorEvent(event) ? '⚙️' : '🏥')}
                    </span>
                    <div className="changed-by-details">
                      <div className={`changed-by-name ${getChangedByBadgeClass(event)}`}>
                        {getChangedByDisplayName(event)}
                      </div>
                      <div className="changed-by-meta">
                        {event.changed_by_employee_id ? (
                          <>
                            <span>ID: {event.changed_by_employee_id}</span>
                            {event.changed_by_role && (
                              <span className="changed-by-role">
                                {formatRoleName(event.changed_by_role)}
                              </span>
                            )}
                          </>
                        ) : isSystemActorEvent(event) ? (
                          <span className="system-assignment-indicator">Auto-assignment (device technician)</span>
                        ) : (
                          <>
                            <span>ID: {getGuestUserEmployeeId(event) || 'N/A'}</span>
                            <span className="guest-indicator">Manual</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </div>

              {/* Elapsed Time Visual Connector - shown AFTER event, between current and next event */}
              {elapsedTimeToNext && (
                <div className="timeline-connector-wrapper">
                  <div className="timeline-connector-line"></div>
                  <div className="timeline-connector-label">{elapsedTimeToNext}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ComplaintTimeline;
